import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { Can } from '../../auth/Can.js';
import type { AccountNode } from '../../types/bookkeeping.js';
import { bankingErrorMessage, flattenTree } from './shared.js';

interface BankRule {
  id: string;
  match_text: string;
  account_id: string;
  is_active: boolean;
}

export function BankRulesPage() {
  const { t } = useTranslation('bookkeeping');
  const qc = useQueryClient();
  const { activeOrgId } = useAuth();

  const [matchText, setMatchText] = useState('');
  const [accountId, setAccountId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data: accountsData } = useQuery({
    queryKey: ['bookkeeping-accounts', activeOrgId],
    queryFn: () => api<{ tree: AccountNode[] }>('/bookkeeping/accounts'),
    enabled: !!activeOrgId,
  });
  const allAccounts = flattenTree(accountsData?.tree ?? []);
  const activeAccounts = allAccounts.filter((a) => a.is_active);
  const accountsById = new Map(allAccounts.map((a) => [a.id, a]));

  const rulesQ = useQuery({
    queryKey: ['bk-bank-rules'],
    queryFn: () => api<BankRule[]>('/bookkeeping/banking/rules'),
    enabled: !!activeOrgId,
  });
  const rules = rulesQ.data ?? [];

  const createMut = useMutation({
    mutationFn: () =>
      api<BankRule>('/bookkeeping/banking/rules', {
        method: 'POST',
        body: JSON.stringify({ match_text: matchText, account_id: accountId }),
      }),
    onSuccess: async () => {
      setError(null);
      setMatchText('');
      setAccountId('');
      await qc.invalidateQueries({ queryKey: ['bk-bank-rules'] });
    },
    onError: (e: unknown) => {
      const err = e as { body?: { error?: string }; message?: string };
      setError(bankingErrorMessage(t, err.body?.error ?? err.message));
    },
  });

  const toggleActiveMut = useMutation({
    mutationFn: (vars: { id: string; is_active: boolean }) =>
      api(`/bookkeeping/banking/rules/${vars.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ is_active: vars.is_active }),
      }),
    onSuccess: async () => {
      setError(null);
      await qc.invalidateQueries({ queryKey: ['bk-bank-rules'] });
    },
    onError: (e: unknown) => {
      const err = e as { body?: { error?: string }; message?: string };
      setError(bankingErrorMessage(t, err.body?.error ?? err.message));
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => api(`/bookkeeping/banking/rules/${id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      setError(null);
      await qc.invalidateQueries({ queryKey: ['bk-bank-rules'] });
    },
    onError: (e: unknown) => {
      const err = e as { body?: { error?: string }; message?: string };
      setError(bankingErrorMessage(t, err.body?.error ?? err.message));
    },
  });

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold">{t('banking.rules_title')}</h1>

      {error && (
        <p className="rounded border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700">
          {error}
        </p>
      )}

      <div className="grid grid-cols-3 gap-3 rounded border bg-white p-4 text-sm">
        <label>
          {t('banking.rule_text')}
          <input
            type="text"
            value={matchText}
            onChange={(e) => setMatchText(e.target.value)}
            className="mt-1 block w-full rounded border px-2 py-1"
          />
        </label>
        <label>
          {t('banking.rule_account')}
          <select
            value={accountId}
            onChange={(e) => setAccountId(e.target.value)}
            className="mt-1 block w-full rounded border px-2 py-1"
          >
            <option value="">—</option>
            {activeAccounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.code} {a.name}
              </option>
            ))}
          </select>
        </label>
        <div className="flex items-end">
          <Can I="manage" a="Account">
            <button
              type="button"
              onClick={() => createMut.mutate()}
              disabled={matchText.trim().length < 2 || !accountId || createMut.isPending}
              className="rounded bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700 disabled:bg-slate-300"
            >
              {t('banking.rule_add')}
            </button>
          </Can>
        </div>
      </div>

      <div className="overflow-x-auto rounded border">
        <table className="w-full text-sm">
          <thead className="border-b bg-slate-50 text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="px-3 py-2">{t('banking.rule_text')}</th>
              <th className="px-3 py-2">{t('banking.rule_account')}</th>
              <th className="px-3 py-2">{t('banking.rule_active')}</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {rules.length === 0 && (
              <tr>
                <td colSpan={4} className="px-3 py-4 text-center text-slate-500">
                  {t('banking.no_rows')}
                </td>
              </tr>
            )}
            {rules.map((rule) => {
              const account = accountsById.get(rule.account_id);
              return (
                <tr key={rule.id} className="border-t">
                  <td className="px-3 py-2">{rule.match_text}</td>
                  <td className="px-3 py-2">
                    {account ? `${account.code} ${account.name}` : rule.account_id}
                  </td>
                  <td className="px-3 py-2">{rule.is_active ? '✓' : '—'}</td>
                  <td className="px-3 py-2">
                    <div className="flex gap-3">
                      <Can I="manage" a="Account">
                        <button
                          type="button"
                          onClick={() =>
                            toggleActiveMut.mutate({ id: rule.id, is_active: !rule.is_active })
                          }
                          className="text-blue-600 hover:underline"
                        >
                          {rule.is_active
                            ? t('banking.rule_deactivate')
                            : t('banking.rule_activate')}
                        </button>
                      </Can>
                      <Can I="manage" a="Account">
                        <button
                          type="button"
                          onClick={() => deleteMut.mutate(rule.id)}
                          className="text-red-600 hover:underline"
                        >
                          {t('banking.rule_delete')}
                        </button>
                      </Can>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
